import { CallSplit, CenterFocusWeak, Close, CropFree, Fullscreen, FullscreenExit, GridView, MoreVert } from "@mui/icons-material";
import { Box, ButtonGroup, GlobalStyles, IconButton, Menu, MenuItem, Tooltip, useTheme } from "@mui/material";
import { useMediaQuery } from "@uidotdev/usehooks";
import { AnimatePresence, motion } from "framer-motion";
import PropTypes from "prop-types";
import { Resizable } from "re-resizable";
import { cloneElement, isValidElement, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useModals } from "../../context/ModalContext";
import { colors } from "../../theme/muiColors";

export const ModalItem = ({
  id,
  index,
  content,
  closeModal,
  minimizeModal,
  restoreModal,
  minimized,
  expandItem,
  windowSize,
  modalLabel,
  modalKey,
  defaultFullscreen = false,
  defaultSizes,
  quickResizePosition = "right",
  bringToFront,
  zIndex,
  onConfirmClose,
  fitContentHeight = false,
  enableFooterDrag = true,
  preventParentTrigger = false,
  onClose,
}) => {
  const theme = useTheme();
  const { showDialog } = useModals();

  const handleClose = useCallback(() => {
    try {
      onClose?.();
    } finally {
      closeModal(id);
    }
  }, [id, closeModal, onClose]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        if (onConfirmClose) {
          showDialog({
            type: "confirm",
            title: "Close modal?",
            message: "Are you sure you want to close this modal? Unsaved changes will be lost.",
            confirmLabel: "Yes, close",
            cancelLabel: "Cancel",
            onConfirm: () => {
              const allowClose = onConfirmClose?.() !== false;
              if (!allowClose) {
                return;
              }
              setTimeout(() => {
                handleClose();
              }, 0);
            },
          });
        } else {
          handleClose();
        }
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [showDialog, onConfirmClose, handleClose]);

  const mdPx = theme.breakpoints.values.md ?? 900;
  const isMediumScreen = useMediaQuery(`(max-width: ${mdPx}px)`);

  // Detectar breakpoints para soporte responsive en defaultSizes
  const xsPx = theme.breakpoints.values.xs ?? 0;
  const smPx = theme.breakpoints.values.sm ?? 600;
  const lgPx = theme.breakpoints.values.lg ?? 1200;
  const isXs = useMediaQuery(`(max-width: ${smPx - 1}px)`);
  const isSm = useMediaQuery(`(min-width: ${smPx}px) and (max-width: ${mdPx - 1}px)`);
  const isMd = useMediaQuery(`(min-width: ${mdPx}px) and (max-width: ${lgPx - 1}px)`);
  const isLg = useMediaQuery(`(min-width: ${lgPx}px)`);

  // Helper para obtener valor responsive de defaultSizes
  const getResponsiveValue = (value) => {
    if (!value) return null;
    if (typeof value === "number" || typeof value === "string") return value;
    if (typeof value === "object") {
      if (isXs && value.xs !== undefined) return value.xs;
      if (isSm && value.sm !== undefined) return value.sm;
      if (isMd && value.md !== undefined) return value.md;
      if (isLg && value.lg !== undefined) return value.lg;
      // Fallback al valor más grande disponible
      return value.lg || value.md || value.sm || value.xs || null;
    }
    return null;
  };

  // Procesar defaultSizes responsive - recalcula cuando cambian los breakpoints
  const processedDefaultSizes = useMemo(
    () => ({
      width: getResponsiveValue(defaultSizes?.width),
      height: getResponsiveValue(defaultSizes?.height),
    }),
    [defaultSizes, isXs, isSm, isMd, isLg],
  );

  const [resizing, setResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(defaultFullscreen);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [currentPosition, setCurrentPosition] = useState({ x: 0, y: 0 });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [previousExpandState, setPreviousExpandState] = useState(expandItem);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuAnchorPosition, setMenuAnchorPosition] = useState(null);
  const [isSmallModal, setIsSmallModal] = useState(false);
  const previousSizeRef = useRef({ width: 0, height: 0 });
  const resizeDirectionRef = useRef({ left: false, top: false });
  const resizableRef = useRef(null);
  const contentContainerRef = useRef(null);
  const contentInnerRef = useRef(null);
  const previousMinimizedRef = useRef(minimized);
  const isSnappingRef = useRef(false);
  const wasDraggingRef = useRef(false);

  const sidebarWidth = isMediumScreen ? 0 : expandItem ? 280 : 100;
  const TOPBAR_HEIGHT = isMediumScreen ? 0 : 40;
  const BORDER_MARGIN = isMediumScreen ? 0 : 12;
  const HEADER_HEIGHT = isMediumScreen ? 0 : 48;
  const FOOTER_HEIGHT = isMediumScreen ? 0 : 20;
  const MODAL_TOP_GAP = isMediumScreen ? 0 : 40;

  const getAvailable = () => ({
    w: windowSize.width - sidebarWidth,
    h: windowSize.height - TOPBAR_HEIGHT,
  });

  const getMaxModalHeight = (height) => Math.max(200, height - BORDER_MARGIN * 2 - MODAL_TOP_GAP);

  const calculateResponsiveSizes = () => {
    const { w, h } = getAvailable();
    const sw = windowSize.width;
    let rw, rh;

    if (sw <= 768) {
      rw = Math.min(w * 0.85, w - BORDER_MARGIN * 2);
      rh = Math.min(h * 0.85, getMaxModalHeight(h));
    } else if (sw <= 1024) {
      rw = Math.min(w * 0.85, 900);
      rh = Math.min(h * 0.8, 650);
    } else {
      rw = Math.min(w * 0.75, 1300);
      rh = Math.min(h * 0.8, 850);
    }

    // Usar valores procesados de defaultSizes (soporte responsive)
    if (processedDefaultSizes.width !== null) {
      const widthValue = typeof processedDefaultSizes.width === "string" ? parseFloat(processedDefaultSizes.width) : processedDefaultSizes.width;
      if (typeof processedDefaultSizes.width === "string" && processedDefaultSizes.width.includes("%")) {
        rw = (w * parseFloat(processedDefaultSizes.width)) / 100;
      } else {
        rw = Math.min(widthValue, rw);
      }
    }

    if (processedDefaultSizes.height !== null) {
      const heightValue = typeof processedDefaultSizes.height === "string" ? parseFloat(processedDefaultSizes.height) : processedDefaultSizes.height;
      if (typeof processedDefaultSizes.height === "string" && processedDefaultSizes.height.includes("%")) {
        rh = (h * parseFloat(processedDefaultSizes.height)) / 100;
      } else {
        rh = Math.min(heightValue, rh);
      }
    }

    return { width: Math.max(rw, 300), height: Math.max(rh, 200) };
  };

  const fitHeightToContent = () => {
    if (!fitContentHeight) return;
    if (isFullscreen || minimized || isMediumScreen) return;
    if (resizing || isTransitioning) return; // No ajustar durante resize o transiciones
    if (isSnappingRef.current) return; // No ajustar durante snap
    if (!resizableRef.current || !contentInnerRef.current) return;

    // Verificar si hay una posición de snap activa
    const modalEl = resizableRef.current?.resizable?.parentElement;
    if (modalEl && modalEl.dataset.snapPosition) return; // No ajustar si hay snap activo

    const el = contentInnerRef.current;
    const maxHeight = getMaxModalHeight(getAvailable().h);
    // The resizable height must include the header and footer areas (if footer is enabled).
    const footerHeight = enableFooterDrag ? FOOTER_HEIGHT : 0;
    const contentHeight = HEADER_HEIGHT + footerHeight + (el.scrollHeight || 0);
    const desired = Math.min(maxHeight, contentHeight);
    const nextHeight = Math.max(200, desired);

    const cur = resizableRef.current?.size;
    const nextWidth = cur?.width ?? calculateResponsiveSizes().width;

    // Si hay un defaultSizes.height definido, no reducir la altura por debajo de ese valor
    if (processedDefaultSizes.height !== null) {
      const defaultHeightValue =
        typeof processedDefaultSizes.height === "string" ? parseFloat(processedDefaultSizes.height) : processedDefaultSizes.height;

      if (typeof processedDefaultSizes.height === "string" && processedDefaultSizes.height.includes("%")) {
        const { h } = getAvailable();
        const minHeight = (h * parseFloat(processedDefaultSizes.height)) / 100;
        // Solo aumentar la altura si el contenido es más grande, no reducirla
        if (cur?.height && nextHeight < cur.height && cur.height >= minHeight * 0.9) {
          return; // No reducir si ya está cerca del tamaño por defecto
        }
      } else {
        // Para valores fijos, no reducir por debajo del valor por defecto
        if (cur?.height && nextHeight < cur.height && cur.height >= defaultHeightValue * 0.9) {
          return; // No reducir si ya está cerca del tamaño por defecto
        }
      }
    }

    // Avoid excessive updates - solo actualizar si el cambio es significativo (más de 5px)
    if (cur?.height && Math.abs(cur.height - nextHeight) < 5) return;

    resizableRef.current.updateSize({ width: nextWidth, height: nextHeight });
  };

  useEffect(() => {
    if (!fitContentHeight) return;
    if (isFullscreen || minimized || isMediumScreen) return;
    const el = contentInnerRef.current;
    if (!el) return;

    // Initial fit after mount / content render (solo una vez al montar)
    const raf = window.requestAnimationFrame(() => {
      if (!resizing && !isTransitioning && !isSnappingRef.current) {
        fitHeightToContent();
      }
    });

    // Debounce para evitar actualizaciones excesivas
    let timeoutId = null;
    const debouncedFitHeight = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        if (!resizing && !isTransitioning && !isSnappingRef.current) {
          window.requestAnimationFrame(() => fitHeightToContent());
        }
      }, 150); // Debounce de 150ms para evitar actualizaciones excesivas
    };

    // Keep fitting when content changes size
    const ro = new ResizeObserver(debouncedFitHeight);
    ro.observe(el);

    return () => {
      window.cancelAnimationFrame(raf);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      ro.disconnect();
    };
  }, [
    fitContentHeight,
    isFullscreen,
    minimized,
    isMediumScreen,
    expandItem,
    windowSize.width,
    windowSize.height,
    enableFooterDrag,
    resizing,
    isTransitioning,
  ]);

  // Detectar cuando el modal es pequeño para mostrar menú en lugar de botones
  useEffect(() => {
    const checkModalSize = () => {
      const currentSize = resizableRef.current?.size;
      if (currentSize) {
        const SMALL_MODAL_THRESHOLD = 500; // px
        setIsSmallModal(currentSize.width < SMALL_MODAL_THRESHOLD);
      }
    };

    // Verificar tamaño inicial
    checkModalSize();

    // Observar cambios en el tamaño
    const resizeObserver = new ResizeObserver(() => {
      checkModalSize();
    });

    if (resizableRef.current?.resizable) {
      resizeObserver.observe(resizableRef.current.resizable);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const getCurrentModalPosition = () => {
    const el = resizableRef.current?.resizable?.parentElement;
    if (!el) return { x: currentPosition.x, y: currentPosition.y };
    const r = el.getBoundingClientRect();
    return { x: r.left, y: r.top };
  };

  const updateModalPosition = ({ left, top }) => {
    const el = resizableRef.current?.resizable?.parentElement;
    if (el) {
      if (!isTransitioning) {
        el.style.top = `${top}px`;
        el.style.left = `${left}px`;
      }
      setCurrentPosition({ x: left, y: top });
    }
  };

  useEffect(() => {
    if (defaultFullscreen && resizableRef.current) {
      const { w, h } = getAvailable();
      const newSize = { width: w - BORDER_MARGIN * 2, height: getMaxModalHeight(h) };

      // Calcular posición con restricciones para asegurar que quepa en el viewport
      const calculatedTop = TOPBAR_HEIGHT + BORDER_MARGIN;
      const calculatedLeft = sidebarWidth + BORDER_MARGIN;

      // Aplicar restricciones
      const minX = sidebarWidth + BORDER_MARGIN;
      const minY = TOPBAR_HEIGHT + BORDER_MARGIN;
      const maxX = windowSize.width - newSize.width - BORDER_MARGIN;
      const maxY = windowSize.height - newSize.height - BORDER_MARGIN;

      const newPos = {
        top: Math.max(minY, Math.min(calculatedTop, maxY)),
        left: Math.max(minX, Math.min(calculatedLeft, maxX)),
      };

      resizableRef.current.updateSize(newSize);
      updateModalPosition(newPos);
    }
  }, [defaultFullscreen, windowSize.width, windowSize.height, sidebarWidth, TOPBAR_HEIGHT, BORDER_MARGIN]);

  // Asegurar que la posición inicial se establezca correctamente después del montaje
  // Usar useLayoutEffect para ejecutar antes del paint
  useLayoutEffect(() => {
    if (!resizableRef.current || minimized || isMediumScreen || isSnappingRef.current) return;

    const el = resizableRef.current.resizable?.parentElement;
    if (!el) return;

    let frameCount = 0;
    const maxFrames = 5; // Intentar hasta 5 frames para asegurar que el tamaño esté disponible

    const checkAndCorrectPosition = () => {
      // Verificar el flag antes de hacer cualquier cosa
      if (isSnappingRef.current) return;

      if (!resizableRef.current) {
        frameCount++;
        if (frameCount < maxFrames) {
          requestAnimationFrame(checkAndCorrectPosition);
        }
        return;
      }

      const currentSize = resizableRef.current.size;
      // Si el tamaño no está disponible, esperar otro frame
      if (!currentSize || currentSize.width === 0 || currentSize.height === 0) {
        frameCount++;
        if (frameCount < maxFrames) {
          requestAnimationFrame(checkAndCorrectPosition);
        }
        return;
      }

      // Verificar el flag nuevamente antes de recalcular
      if (isSnappingRef.current) return;

      // Si hay una posición de snap guardada, no recalcular
      if (el && el.dataset.snapPosition) return;

      const currentRect = el.getBoundingClientRect();
      const currentLeft = parseFloat(el.style.left) || currentRect.left;
      const currentTop = parseFloat(el.style.top) || currentRect.top;

      // Verificar si la posición actual es válida dentro del safeZone
      const minX = sidebarWidth + BORDER_MARGIN;
      const minY = TOPBAR_HEIGHT + BORDER_MARGIN;
      const maxX = windowSize.width - currentSize.width - BORDER_MARGIN;
      const maxY = windowSize.height - currentSize.height - BORDER_MARGIN;

      // Si la posición actual está dentro del safeZone y es válida, no recalcular
      if (currentLeft >= minX && currentLeft <= maxX && currentTop >= minY && currentTop <= maxY && !isFullscreen && !defaultFullscreen) {
        // La posición actual es válida, no hacer nada
        return;
      }

      let calculatedTop, calculatedLeft;

      // Calcular posición según el tipo de modal
      if (isFullscreen || defaultFullscreen) {
        calculatedTop = TOPBAR_HEIGHT + BORDER_MARGIN;
        calculatedLeft = sidebarWidth + BORDER_MARGIN;
      } else {
        const { w, h } = getAvailable();
        calculatedTop = Math.max(TOPBAR_HEIGHT + (h - currentSize.height) / 2, TOPBAR_HEIGHT + BORDER_MARGIN);
        calculatedLeft = Math.max(sidebarWidth + (w - currentSize.width) / 2, sidebarWidth + BORDER_MARGIN);
      }

      const constrainedLeft = Math.max(minX, Math.min(calculatedLeft, maxX));
      const constrainedTop = Math.max(minY, Math.min(calculatedTop, maxY));

      // Solo actualizar si la posición calculada es diferente a la actual
      if (Math.abs(constrainedLeft - currentLeft) > 1 || Math.abs(constrainedTop - currentTop) > 1) {
        // Forzar actualización de posición para asegurar que esté correcta
        el.style.left = `${constrainedLeft}px`;
        el.style.top = `${constrainedTop}px`;
        setCurrentPosition({ x: constrainedLeft, y: constrainedTop });
      }
    };

    // Iniciar la verificación
    requestAnimationFrame(checkAndCorrectPosition);
  }, [
    isFullscreen,
    defaultFullscreen,
    minimized,
    isMediumScreen,
    windowSize.width,
    windowSize.height,
    sidebarWidth,
    TOPBAR_HEIGHT,
    BORDER_MARGIN,
    expandItem,
  ]);

  // Efecto adicional para corregir la posición después del primer render (para fullscreen)
  useEffect(() => {
    if (!defaultFullscreen) return;

    // Esperar a que el DOM esté completamente renderizado
    const timeoutId = setTimeout(() => {
      if (!resizableRef.current) return;

      const el = resizableRef.current.resizable?.parentElement;
      if (!el) return;

      const currentSize = resizableRef.current.size || { width: 0, height: 0 };
      const currentRect = el.getBoundingClientRect();

      // Calcular posición correcta con restricciones
      const calculatedTop = TOPBAR_HEIGHT + BORDER_MARGIN;
      const calculatedLeft = sidebarWidth + BORDER_MARGIN;

      const minX = sidebarWidth + BORDER_MARGIN;
      const minY = TOPBAR_HEIGHT + BORDER_MARGIN;
      const maxX = windowSize.width - currentSize.width - BORDER_MARGIN;
      const maxY = windowSize.height - currentSize.height - BORDER_MARGIN;

      const constrainedLeft = Math.max(minX, Math.min(calculatedLeft, maxX));
      const constrainedTop = Math.max(minY, Math.min(calculatedTop, maxY));

      // Forzar actualización de posición
      el.style.left = `${constrainedLeft}px`;
      el.style.top = `${constrainedTop}px`;
      setCurrentPosition({ x: constrainedLeft, y: constrainedTop });
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [defaultFullscreen, windowSize.width, windowSize.height, sidebarWidth, TOPBAR_HEIGHT, BORDER_MARGIN]);

  // Efecto para corregir la posición de modales normales después del primer render
  useEffect(() => {
    if (defaultFullscreen || isFullscreen || minimized || isMediumScreen || isSnappingRef.current) return;

    let frameCount = 0;
    const maxFrames = 5; // Intentar hasta 5 frames para asegurar que el tamaño esté disponible

    const checkAndCorrectPosition = () => {
      if (!resizableRef.current) {
        frameCount++;
        if (frameCount < maxFrames) {
          requestAnimationFrame(checkAndCorrectPosition);
        }
        return;
      }

      const el = resizableRef.current.resizable?.parentElement;
      if (!el) {
        frameCount++;
        if (frameCount < maxFrames) {
          requestAnimationFrame(checkAndCorrectPosition);
        }
        return;
      }

      const currentSize = resizableRef.current.size;
      // Si el tamaño no está disponible, esperar otro frame
      if (!currentSize || currentSize.width === 0 || currentSize.height === 0) {
        frameCount++;
        if (frameCount < maxFrames) {
          requestAnimationFrame(checkAndCorrectPosition);
        }
        return;
      }

      const currentRect = el.getBoundingClientRect();
      const currentLeft = parseFloat(el.style.left) || currentRect.left;
      const currentTop = parseFloat(el.style.top) || currentRect.top;

      // Verificar si la posición actual es válida dentro del safeZone
      const minX = sidebarWidth + BORDER_MARGIN;
      const minY = TOPBAR_HEIGHT + BORDER_MARGIN;
      const maxX = windowSize.width - currentSize.width - BORDER_MARGIN;
      const maxY = windowSize.height - currentSize.height - BORDER_MARGIN;

      // Si la posición actual está dentro del safeZone y es válida, no recalcular
      if (currentLeft >= minX && currentLeft <= maxX && currentTop >= minY && currentTop <= maxY) {
        // La posición actual es válida, no hacer nada
        return;
      }

      // Calcular posición centrada con restricciones
      const { w, h } = getAvailable();
      const calculatedTop = Math.max(TOPBAR_HEIGHT + (h - currentSize.height) / 2, TOPBAR_HEIGHT + BORDER_MARGIN);
      const calculatedLeft = Math.max(sidebarWidth + (w - currentSize.width) / 2, sidebarWidth + BORDER_MARGIN);

      const constrainedLeft = Math.max(minX, Math.min(calculatedLeft, maxX));
      const constrainedTop = Math.max(minY, Math.min(calculatedTop, maxY));

      // Solo actualizar si la posición calculada es diferente a la actual
      if (Math.abs(constrainedLeft - currentLeft) > 1 || Math.abs(constrainedTop - currentTop) > 1) {
        // Forzar actualización de posición para asegurar que esté correcta
        el.style.left = `${constrainedLeft}px`;
        el.style.top = `${constrainedTop}px`;
        setCurrentPosition({ x: constrainedLeft, y: constrainedTop });
      }
    };

    // Iniciar la verificación después de un pequeño delay para asegurar que el DOM esté listo
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(checkAndCorrectPosition);
    }, 10);

    return () => clearTimeout(timeoutId);
  }, [windowSize.width, windowSize.height, sidebarWidth, TOPBAR_HEIGHT, BORDER_MARGIN, expandItem]);

  // ResizeObserver para corregir posición cuando el tamaño del modal cambia
  useEffect(() => {
    if (minimized || isMediumScreen || isSnappingRef.current) return;
    if (!resizableRef.current) return;

    const el = resizableRef.current.resizable?.parentElement;
    if (!el) return;

    const correctPosition = () => {
      if (!resizableRef.current || isSnappingRef.current) return;

      // Si hay una posición de snap guardada, no recalcular
      if (el && el.dataset.snapPosition) return;

      const currentSize = resizableRef.current.size;
      if (!currentSize || currentSize.width === 0 || currentSize.height === 0) return;

      const currentRect = el.getBoundingClientRect();
      const currentLeft = parseFloat(el.style.left) || currentRect.left;
      const currentTop = parseFloat(el.style.top) || currentRect.top;

      // Verificar si la posición actual es válida dentro del safeZone
      const minX = sidebarWidth + BORDER_MARGIN;
      const minY = TOPBAR_HEIGHT + BORDER_MARGIN;
      const maxX = windowSize.width - currentSize.width - BORDER_MARGIN;
      const maxY = windowSize.height - currentSize.height - BORDER_MARGIN;

      // Si la posición actual está dentro del safeZone y es válida, no recalcular
      if (currentLeft >= minX && currentLeft <= maxX && currentTop >= minY && currentTop <= maxY && !isFullscreen && !defaultFullscreen) {
        // La posición actual es válida, no hacer nada
        return;
      }

      let calculatedTop, calculatedLeft;

      if (isFullscreen || defaultFullscreen) {
        calculatedTop = TOPBAR_HEIGHT + BORDER_MARGIN;
        calculatedLeft = sidebarWidth + BORDER_MARGIN;
      } else {
        const { w, h } = getAvailable();
        calculatedTop = Math.max(TOPBAR_HEIGHT + (h - currentSize.height) / 2, TOPBAR_HEIGHT + BORDER_MARGIN);
        calculatedLeft = Math.max(sidebarWidth + (w - currentSize.width) / 2, sidebarWidth + BORDER_MARGIN);
      }

      const constrainedLeft = Math.max(minX, Math.min(calculatedLeft, maxX));
      const constrainedTop = Math.max(minY, Math.min(calculatedTop, maxY));

      // Solo actualizar si la posición calculada es diferente a la actual
      if (Math.abs(constrainedLeft - currentLeft) > 1 || Math.abs(constrainedTop - currentTop) > 1) {
        el.style.left = `${constrainedLeft}px`;
        el.style.top = `${constrainedTop}px`;
        setCurrentPosition({ x: constrainedLeft, y: constrainedTop });
      }
    };

    // Observar cambios en el tamaño del modal
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(correctPosition);
    });

    if (resizableRef.current.resizable) {
      resizeObserver.observe(resizableRef.current.resizable);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    minimized,
    isMediumScreen,
    isFullscreen,
    defaultFullscreen,
    windowSize.width,
    windowSize.height,
    sidebarWidth,
    TOPBAR_HEIGHT,
    BORDER_MARGIN,
    expandItem,
  ]);

  useEffect(() => {
    if (isMediumScreen || minimized || isSnappingRef.current) return;
    if (!resizableRef.current) return;

    const el = resizableRef.current.resizable?.parentElement;
    // Si hay una posición de snap guardada, no recalcular
    if (el && el.dataset.snapPosition) return;

    const { width, height } = calculateResponsiveSizes();
    const { w, h } = getAvailable();

    // Calcular posición centrada
    const top = Math.max(TOPBAR_HEIGHT + (h - height) / 2, TOPBAR_HEIGHT + BORDER_MARGIN);
    const left = Math.max(sidebarWidth + (w - width) / 2, sidebarWidth + BORDER_MARGIN);

    // Asegurar que no se salga del viewport
    const maxX = windowSize.width - width - BORDER_MARGIN;
    const maxY = windowSize.height - height - BORDER_MARGIN;
    const constrainedLeft = Math.max(sidebarWidth + BORDER_MARGIN, Math.min(left, maxX));
    const constrainedTop = Math.max(TOPBAR_HEIGHT + BORDER_MARGIN, Math.min(top, maxY));

    // Actualizar posición en el DOM
    if (el) {
      el.style.left = `${constrainedLeft}px`;
      el.style.top = `${constrainedTop}px`;
    }

    setCurrentPosition({ x: constrainedLeft, y: constrainedTop });
  }, [isMediumScreen, minimized, windowSize.width, windowSize.height, expandItem]);

  // Manejar restauración del modal: centrar y ajustar tamaño
  useEffect(() => {
    // Detectar cuando el modal se restaura (cambia de minimized: true a false)
    const wasMinimized = previousMinimizedRef.current;
    const isRestored = wasMinimized && !minimized;

    if (isRestored && !isMediumScreen && !isFullscreen && resizableRef.current) {
      const { w, h } = getAvailable();
      let { width, height } = calculateResponsiveSizes();

      // Ajustar tamaño si es necesario para que quepa en el viewport
      const maxWidth = w - BORDER_MARGIN * 2;
      const maxHeight = getMaxModalHeight(h);

      if (width > maxWidth) width = maxWidth;
      if (height > maxHeight) height = maxHeight;

      // Asegurar tamaño mínimo
      width = Math.max(width, 300);
      height = Math.max(height, 200);

      // Calcular posición centrada
      const top = Math.max(TOPBAR_HEIGHT + (h - height) / 2, TOPBAR_HEIGHT + BORDER_MARGIN);
      const left = Math.max(sidebarWidth + (w - width) / 2, sidebarWidth + BORDER_MARGIN);

      // Asegurar que no se salga del viewport
      const maxX = windowSize.width - width - BORDER_MARGIN;
      const maxY = windowSize.height - height - BORDER_MARGIN;
      const constrainedLeft = Math.max(sidebarWidth + BORDER_MARGIN, Math.min(left, maxX));
      const constrainedTop = Math.max(TOPBAR_HEIGHT + BORDER_MARGIN, Math.min(top, maxY));

      // Actualizar tamaño y posición
      resizableRef.current.updateSize({ width, height });

      const el = resizableRef.current.resizable?.parentElement;
      if (el) {
        el.style.left = `${constrainedLeft}px`;
        el.style.top = `${constrainedTop}px`;
        setCurrentPosition({ x: constrainedLeft, y: constrainedTop });
      }
    }

    // Actualizar el ref para la próxima vez
    previousMinimizedRef.current = minimized;
  }, [minimized, isMediumScreen, isFullscreen, windowSize.width, windowSize.height, expandItem, sidebarWidth, TOPBAR_HEIGHT, BORDER_MARGIN]);

  useEffect(() => {
    if (isMediumScreen) return;

    if (previousExpandState !== expandItem && !isFullscreen) {
      setIsTransitioning(true);
      const minLeft = (expandItem ? 280 : 100) + BORDER_MARGIN;
      const cur = getCurrentModalPosition();
      let newLeft = cur.x;
      if (newLeft < minLeft) newLeft = minLeft;

      const el = resizableRef.current?.resizable?.parentElement;
      if (el && newLeft !== cur.x) {
        el.style.transition = "left 0.3s cubic-bezier(0.4, 0, 0.2, 1)";
        el.style.left = `${newLeft}px`;
        setCurrentPosition({ x: newLeft, y: cur.y });
        setTimeout(() => {
          el.style.transition = "";
          setIsTransitioning(false);
        }, 300);
      } else {
        setIsTransitioning(false);
      }
      setPreviousExpandState(expandItem);
    }

    if (previousExpandState !== expandItem && isFullscreen) {
      setIsTransitioning(true);
      const { w, h } = getAvailable();
      const newSize = { width: w - BORDER_MARGIN * 2, height: getMaxModalHeight(h) };

      // Calcular posición con restricciones
      const calculatedTop = TOPBAR_HEIGHT + BORDER_MARGIN;
      const calculatedLeft = sidebarWidth + BORDER_MARGIN;

      // Aplicar restricciones
      const minX = sidebarWidth + BORDER_MARGIN;
      const minY = TOPBAR_HEIGHT + BORDER_MARGIN;
      const maxX = windowSize.width - newSize.width - BORDER_MARGIN;
      const maxY = windowSize.height - newSize.height - BORDER_MARGIN;

      const newPos = {
        top: Math.max(minY, Math.min(calculatedTop, maxY)),
        left: Math.max(minX, Math.min(calculatedLeft, maxX)),
      };

      if (resizableRef.current) {
        resizableRef.current.updateSize(newSize);
        const el = resizableRef.current.resizable?.parentElement;
        if (el) {
          el.style.transition = "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)";
          el.style.left = `${newPos.left}px`;
          el.style.top = `${newPos.top}px`;
          setCurrentPosition({ x: newPos.left, y: newPos.top });
          setTimeout(() => {
            el.style.transition = "";
            setIsTransitioning(false);
          }, 300);
        }
      }
      setPreviousExpandState(expandItem);
    }
  }, [isMediumScreen, expandItem, previousExpandState, windowSize, isFullscreen]);

  // Asegurar que el estado de resizing se limpie si el usuario suelta el mouse fuera del área
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (resizing) {
        resizeDirectionRef.current = { left: false, top: false };
        setResizing(false);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    };

    if (resizing) {
      window.addEventListener("mouseup", handleGlobalMouseUp, { capture: true });
      return () => {
        window.removeEventListener("mouseup", handleGlobalMouseUp, { capture: true });
      };
    }
  }, [resizing]);

  // Handlers para drag manual
  const handleMouseDown = (e) => {
    // Si preventParentTrigger está activo, detener la propagación antes de cualquier otra lógica
    if (preventParentTrigger) {
      e.stopPropagation();
      if (e.stopImmediatePropagation && typeof e.stopImmediatePropagation === "function") {
        e.stopImmediatePropagation();
      }
    } else {
      // Detener la propagación para evitar que otros modales también se muevan
      e.stopPropagation();
    }

    if (!e.target.closest("button") && !resizing && !isTransitioning) {
      const el = resizableRef.current?.resizable?.parentElement;
      if (el) {
        // Verificar que el click fue directamente en el header o footer de este modal
        // Buscar el elemento más cercano con data-modal-drag-handle
        const dragHandle = e.target.closest("[data-modal-drag-handle]");

        // Solo activar si el click es en un área de drag (header o footer) de este modal específico
        if (dragHandle && el.contains(dragHandle)) {
          // Verificar que no hay un modal hijo con z-index mayor en el punto del click
          const clickX = e.clientX;
          const clickY = e.clientY;
          const allModals = document.querySelectorAll('[style*="position: fixed"][style*="z-index"]');
          let hasModalAbove = false;

          allModals.forEach((modal) => {
            if (modal === el) return;
            const modalZIndex = parseInt(window.getComputedStyle(modal).zIndex) || 0;
            const currentZIndex = parseInt(window.getComputedStyle(el).zIndex) || 0;
            const rect = modal.getBoundingClientRect();

            // Si hay un modal con z-index mayor en el punto del click, no activar drag
            if (modalZIndex > currentZIndex && clickX >= rect.left && clickX <= rect.right && clickY >= rect.top && clickY <= rect.bottom) {
              hasModalAbove = true;
            }
          });

          if (!hasModalAbove) {
            setIsDragging(true);
            wasDraggingRef.current = true;
            bringToFront(id);
            const rect = el.getBoundingClientRect();
            dragStartPos.current = {
              x: e.clientX - rect.left,
              y: e.clientY - rect.top,
            };
          }
        }
      }
    }
  };

  // useEffect para manejar el drag - debe estar antes de cualquier return temprano
  useEffect(() => {
    if (!isDragging) return;

    // Guardar referencia al elemento que está siendo arrastrado
    const draggedElement = resizableRef.current?.resizable?.parentElement;
    if (!draggedElement) return;

    const handleMouseMove = (e) => {
      // Verificar que el elemento todavía existe y es el correcto
      if (!draggedElement || !resizableRef.current?.resizable?.parentElement) return;
      if (draggedElement !== resizableRef.current?.resizable?.parentElement) return;

      if (!isTransitioning && !resizing) {
        const newX = e.clientX - dragStartPos.current.x;
        const newY = e.clientY - dragStartPos.current.y;

        // Asegurar que el modal no salga de los límites
        const currentSize = resizableRef.current?.size || { width: 0, height: 0 };
        const minX = sidebarWidth + BORDER_MARGIN;
        const minY = TOPBAR_HEIGHT + BORDER_MARGIN;
        const maxX = windowSize.width - currentSize.width - BORDER_MARGIN;
        const maxY = windowSize.height - currentSize.height - BORDER_MARGIN;

        const constrainedX = Math.max(minX, Math.min(newX, maxX));
        const constrainedY = Math.max(minY, Math.min(newY, maxY));

        // Solo actualizar el elemento específico que está siendo arrastrado
        draggedElement.style.left = `${constrainedX}px`;
        draggedElement.style.top = `${constrainedY}px`;
        setCurrentPosition({ x: constrainedX, y: constrainedY });
      }
    };

    const handleMouseUp = (e) => {
      const wasDragging = wasDraggingRef.current;

      // Si preventParentTrigger está activo y se hizo drag, detener la propagación para evitar que se active el modal padre
      if (preventParentTrigger && wasDragging) {
        if (e?.stopPropagation && typeof e.stopPropagation === "function") {
          e.stopPropagation();
        }
        if (e?.stopImmediatePropagation && typeof e.stopImmediatePropagation === "function") {
          e.stopImmediatePropagation();
        }
        // Prevenir que el evento llegue al Card del modal padre
        if (e?.preventDefault && typeof e.preventDefault === "function") {
          e.preventDefault();
        }
        // Agregar un pequeño delay para asegurar que el evento no se propague
        setTimeout(() => {
          wasDraggingRef.current = false;
        }, 100);
      } else if (!preventParentTrigger) {
        // Detener propagación para evitar que otros modales también respondan
        if (e?.stopPropagation && typeof e.stopPropagation === "function") {
          e.stopPropagation();
        }
      } else {
        // Si preventParentTrigger está activo pero no se hizo drag, resetear el flag
        wasDraggingRef.current = false;
      }

      setIsDragging(false);
    };

    // Si preventParentTrigger está activo, usar capture: true para capturar el evento antes de que llegue al Card
    // Usar capture: false para que los modales hijos puedan capturar primero (comportamiento normal)
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("mouseup", handleMouseUp, preventParentTrigger ? { capture: true } : false);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp, preventParentTrigger ? { capture: true } : false);
    };
  }, [isDragging, isTransitioning, resizing, windowSize, sidebarWidth, BORDER_MARGIN, TOPBAR_HEIGHT, preventParentTrigger]);

  const MOBILE_MARGIN = theme.spacing(2);

  if (!minimized && isMediumScreen) {
    return (
      <Box
        key={id}
        sx={{
          position: "fixed",
          // top: `calc(env(safe-area-inset-top) + ${MOBILE_MARGIN})`,
          top: 72,
          right: `calc(env(safe-area-inset-right) + ${MOBILE_MARGIN})`,
          bottom: `calc(env(safe-area-inset-bottom) + ${MOBILE_MARGIN})`,
          left: `calc(env(safe-area-inset-left) + ${MOBILE_MARGIN})`,
          zIndex: 15000 + index,
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          borderRadius: 2,

          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ fontWeight: 600, fontSize: 15, color: colors.blueGrey[700] }}>{modalLabel}</Box>
          <IconButton sx={{ backgroundColor: colors.blue[200] }} onClick={handleClose} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", p: 2 }}>{content}</Box>
      </Box>
    );
  }

  if (minimized) {
    return null;
  }

  const { w: availableWidth, h: availableHeight } = getAvailable();
  const { width: initialWidth, height: initialHeight } =
    isFullscreen || defaultFullscreen
      ? { width: availableWidth - BORDER_MARGIN * 2, height: getMaxModalHeight(availableHeight) }
      : calculateResponsiveSizes();

  // Calcular posición inicial con restricciones para asegurar que quepa en el viewport
  let calculatedTop, calculatedLeft;
  if (isFullscreen || defaultFullscreen) {
    calculatedTop = TOPBAR_HEIGHT + BORDER_MARGIN;
    calculatedLeft = sidebarWidth + BORDER_MARGIN;
  } else {
    calculatedTop = Math.max(TOPBAR_HEIGHT + (availableHeight - initialHeight) / 2, TOPBAR_HEIGHT + BORDER_MARGIN);
    calculatedLeft = Math.max(sidebarWidth + (availableWidth - initialWidth) / 2, sidebarWidth + BORDER_MARGIN);
  }

  // Aplicar restricciones para asegurar que el modal quepa dentro del viewport
  const minX = sidebarWidth + BORDER_MARGIN;
  const minY = TOPBAR_HEIGHT + BORDER_MARGIN;
  const maxX = windowSize.width - initialWidth - BORDER_MARGIN;
  const maxY = windowSize.height - initialHeight - BORDER_MARGIN;

  const initialTop = Math.max(minY, Math.min(calculatedTop, maxY));
  const initialLeft = Math.max(minX, Math.min(calculatedLeft, maxX));
  const minWidth = Math.min(300, availableWidth * 0.5);
  const minHeight = Math.min(200, availableHeight * 0.3);
  const minimizedParents = ["Dashboard", "Organization"];

  const quickResize = (type) => {
    if (!resizableRef.current) return;

    const currentSidebarWidth = sidebarWidth; // unificado
    const { w, h } = getAvailable();
    const topbar = TOPBAR_HEIGHT;

    const currentPos = getCurrentModalPosition();
    const currentSize = resizableRef.current?.size ?? { width: initialWidth, height: initialHeight };

    let newSize = {};
    let newPosition = {};

    switch (type) {
      case "fullscreen":
        if (isFullscreen) {
          newSize = { width: initialWidth, height: initialHeight };
          newPosition = {
            top: Math.max(topbar + (h - initialHeight) / 2, topbar + BORDER_MARGIN),
            left: Math.max(currentSidebarWidth + (w - initialWidth) / 2, currentSidebarWidth + BORDER_MARGIN),
          };
          setIsFullscreen(false);
        } else {
          newSize = { width: w - BORDER_MARGIN * 2, height: getMaxModalHeight(h) };
          newPosition = { top: topbar + BORDER_MARGIN, left: currentSidebarWidth + BORDER_MARGIN };
          setIsFullscreen(true);
        }
        break;
      case "snap-left":
        isSnappingRef.current = true; // Marcar que estamos haciendo snap ANTES de cualquier cambio
        // Si está en fullscreen, primero desactivarlo
        if (isFullscreen) {
          setIsFullscreen(false);
          // Esperar un frame para que el estado se actualice, luego ejecutar el snap
          requestAnimationFrame(() => {
            newSize = { width: w / 2 - BORDER_MARGIN * 1.5, height: getMaxModalHeight(h) };
            newPosition = { top: topbar + BORDER_MARGIN, left: currentSidebarWidth + BORDER_MARGIN };
            // Actualizar tamaño y posición de forma síncrona
            if (Object.keys(newSize).length) resizableRef.current.updateSize(newSize);
            if (Object.keys(newPosition).length) {
              updateModalPosition(newPosition);
              // Forzar actualización inmediata en el DOM y bloquear cualquier recálculo
              const el = resizableRef.current?.resizable?.parentElement;
              if (el) {
                el.style.left = `${newPosition.left}px`;
                el.style.top = `${newPosition.top}px`;
                el.style.transition = "none";
                el.dataset.snapPosition = JSON.stringify(newPosition);
              }
            }
            setShowQuickActions(false);
            // Limpiar el flag después de un delay
            setTimeout(() => {
              isSnappingRef.current = false;
              const el = resizableRef.current?.resizable?.parentElement;
              if (el) {
                el.style.transition = "";
                el.dataset.snapPosition = "";
              }
            }, 300);
          });
        } else {
          // Si no está en fullscreen, ejecutar el snap directamente
          newSize = { width: w / 2 - BORDER_MARGIN * 1.5, height: getMaxModalHeight(h) };
          newPosition = { top: topbar + BORDER_MARGIN, left: currentSidebarWidth + BORDER_MARGIN };
          // Actualizar tamaño y posición de forma síncrona
          if (Object.keys(newSize).length) resizableRef.current.updateSize(newSize);
          if (Object.keys(newPosition).length) {
            updateModalPosition(newPosition);
            // Forzar actualización inmediata en el DOM y bloquear cualquier recálculo
            const el = resizableRef.current?.resizable?.parentElement;
            if (el) {
              el.style.left = `${newPosition.left}px`;
              el.style.top = `${newPosition.top}px`;
              el.style.transition = "none";
              el.dataset.snapPosition = JSON.stringify(newPosition);
            }
          }
          setShowQuickActions(false);
          // Limpiar el flag después de un delay
          setTimeout(() => {
            isSnappingRef.current = false;
            const el = resizableRef.current?.resizable?.parentElement;
            if (el) {
              el.style.transition = "";
              el.dataset.snapPosition = "";
            }
          }, 300);
        }
        return; // Salir temprano para evitar ejecutar el código de actualización al final
      case "snap-right":
        isSnappingRef.current = true; // Marcar que estamos haciendo snap ANTES de cualquier cambio
        // Si está en fullscreen, primero desactivarlo
        if (isFullscreen) {
          setIsFullscreen(false);
          // Esperar un frame para que el estado se actualice, luego ejecutar el snap
          requestAnimationFrame(() => {
            newSize = { width: w / 2 - BORDER_MARGIN * 1.5, height: getMaxModalHeight(h) };
            newPosition = { top: topbar + BORDER_MARGIN, left: currentSidebarWidth + w / 2 + BORDER_MARGIN / 2 };
            // Actualizar tamaño y posición de forma síncrona
            if (Object.keys(newSize).length) resizableRef.current.updateSize(newSize);
            if (Object.keys(newPosition).length) {
              updateModalPosition(newPosition);
              // Forzar actualización inmediata en el DOM y bloquear cualquier recálculo
              const el = resizableRef.current?.resizable?.parentElement;
              if (el) {
                el.style.left = `${newPosition.left}px`;
                el.style.top = `${newPosition.top}px`;
                el.style.transition = "none";
                el.dataset.snapPosition = JSON.stringify(newPosition);
              }
            }
            setShowQuickActions(false);
            // Limpiar el flag después de un delay
            setTimeout(() => {
              isSnappingRef.current = false;
              const el = resizableRef.current?.resizable?.parentElement;
              if (el) {
                el.style.transition = "";
                el.dataset.snapPosition = "";
              }
            }, 300);
          });
        } else {
          // Si no está en fullscreen, ejecutar el snap directamente
          newSize = { width: w / 2 - BORDER_MARGIN * 1.5, height: getMaxModalHeight(h) };
          newPosition = { top: topbar + BORDER_MARGIN, left: currentSidebarWidth + w / 2 + BORDER_MARGIN / 2 };
          // Actualizar tamaño y posición de forma síncrona
          if (Object.keys(newSize).length) resizableRef.current.updateSize(newSize);
          if (Object.keys(newPosition).length) {
            updateModalPosition(newPosition);
            // Forzar actualización inmediata en el DOM y bloquear cualquier recálculo
            const el = resizableRef.current?.resizable?.parentElement;
            if (el) {
              el.style.left = `${newPosition.left}px`;
              el.style.top = `${newPosition.top}px`;
              el.style.transition = "none";
              el.dataset.snapPosition = JSON.stringify(newPosition);
            }
          }
          setShowQuickActions(false);
          // Limpiar el flag después de un delay
          setTimeout(() => {
            isSnappingRef.current = false;
            const el = resizableRef.current?.resizable?.parentElement;
            if (el) {
              el.style.transition = "";
              el.dataset.snapPosition = "";
            }
          }, 300);
        }
        return; // Salir temprano para evitar ejecutar el código de actualización al final
      case "snap-top-left":
        isSnappingRef.current = true; // Marcar que estamos haciendo snap ANTES de cualquier cambio
        // Si está en fullscreen, primero desactivarlo
        if (isFullscreen) {
          setIsFullscreen(false);
          // Esperar un frame para que el estado se actualice, luego ejecutar el snap
          requestAnimationFrame(() => {
            // Calcular tamaño correcto: (w - 3*BORDER_MARGIN) / 2 para que quepan dos modales lado a lado
            // Para altura: (h - 3*BORDER_MARGIN) / 2 para que quepan dos modales arriba/abajo
            newSize = { width: (w - BORDER_MARGIN * 3) / 2, height: (h - BORDER_MARGIN * 3) / 2 };
            newPosition = { top: topbar + BORDER_MARGIN, left: currentSidebarWidth + BORDER_MARGIN };
            // Actualizar tamaño y posición de forma síncrona
            if (Object.keys(newSize).length) resizableRef.current.updateSize(newSize);
            if (Object.keys(newPosition).length) {
              updateModalPosition(newPosition);
              // Forzar actualización inmediata en el DOM
              const el = resizableRef.current?.resizable?.parentElement;
              if (el) {
                el.style.left = `${newPosition.left}px`;
                el.style.top = `${newPosition.top}px`;
                el.style.transition = "none";
                el.dataset.snapPosition = JSON.stringify(newPosition);
              }
            }
            setShowQuickActions(false);
            // Limpiar el flag después de un delay
            setTimeout(() => {
              isSnappingRef.current = false;
              const el = resizableRef.current?.resizable?.parentElement;
              if (el) {
                el.style.transition = "";
                el.dataset.snapPosition = "";
              }
            }, 300);
          });
          return; // Salir temprano para evitar ejecutar el código de actualización al final
        } else {
          // Si no está en fullscreen, ejecutar el snap directamente
          newSize = { width: w / 2 - BORDER_MARGIN * 1.5, height: h / 2 - BORDER_MARGIN * 1.5 };
          newPosition = { top: topbar + BORDER_MARGIN, left: currentSidebarWidth + BORDER_MARGIN };
          // Actualizar tamaño y posición de forma síncrona
          if (Object.keys(newSize).length) resizableRef.current.updateSize(newSize);
          if (Object.keys(newPosition).length) {
            updateModalPosition(newPosition);
            // Forzar actualización inmediata en el DOM
            const el = resizableRef.current?.resizable?.parentElement;
            if (el) {
              el.style.left = `${newPosition.left}px`;
              el.style.top = `${newPosition.top}px`;
              el.style.transition = "none";
              el.dataset.snapPosition = JSON.stringify(newPosition);
            }
          }
          setShowQuickActions(false);
          // Limpiar el flag después de un delay más largo para evitar interferencias con fitContentHeight
          setTimeout(() => {
            isSnappingRef.current = false;
            const el = resizableRef.current?.resizable?.parentElement;
            if (el) {
              el.style.transition = "";
              el.dataset.snapPosition = "";
            }
          }, 800);
        }
        return; // Salir temprano para evitar ejecutar el código de actualización al final
      case "snap-top-right":
        isSnappingRef.current = true; // Marcar que estamos haciendo snap ANTES de cualquier cambio
        // Si está en fullscreen, primero desactivarlo
        if (isFullscreen) {
          setIsFullscreen(false);
          // Esperar un frame para que el estado se actualice, luego ejecutar el snap
          requestAnimationFrame(() => {
            // Calcular tamaño correcto: (w - 3*BORDER_MARGIN) / 2 para que quepan dos modales lado a lado
            const quarterWidth = (w - BORDER_MARGIN * 3) / 2;
            newSize = { width: quarterWidth, height: (h - BORDER_MARGIN * 3) / 2 };
            newPosition = { top: topbar + BORDER_MARGIN, left: currentSidebarWidth + quarterWidth + BORDER_MARGIN * 2 };
            // Actualizar tamaño y posición de forma síncrona
            if (Object.keys(newSize).length) resizableRef.current.updateSize(newSize);
            if (Object.keys(newPosition).length) {
              updateModalPosition(newPosition);
              // Forzar actualización inmediata en el DOM
              const el = resizableRef.current?.resizable?.parentElement;
              if (el) {
                el.style.left = `${newPosition.left}px`;
                el.style.top = `${newPosition.top}px`;
                el.style.transition = "none";
                el.dataset.snapPosition = JSON.stringify(newPosition);
              }
            }
            setShowQuickActions(false);
            // Limpiar el flag después de un delay más largo para evitar interferencias
            setTimeout(() => {
              isSnappingRef.current = false;
              const el = resizableRef.current?.resizable?.parentElement;
              if (el) {
                el.style.transition = "";
                el.dataset.snapPosition = "";
              }
            }, 800);
          });
          return; // Salir temprano para evitar ejecutar el código de actualización al final
        } else {
          // Si no está en fullscreen, ejecutar el snap directamente
          // Calcular tamaño correcto: (w - 3*BORDER_MARGIN) / 2 para que quepan dos modales lado a lado
          const quarterWidth = (w - BORDER_MARGIN * 3) / 2;
          newSize = { width: quarterWidth, height: (h - BORDER_MARGIN * 3) / 2 };
          newPosition = { top: topbar + BORDER_MARGIN, left: currentSidebarWidth + quarterWidth + BORDER_MARGIN * 2 };
          // Actualizar tamaño y posición de forma síncrona
          if (Object.keys(newSize).length) resizableRef.current.updateSize(newSize);
          if (Object.keys(newPosition).length) {
            updateModalPosition(newPosition);
            // Forzar actualización inmediata en el DOM
            const el = resizableRef.current?.resizable?.parentElement;
            if (el) {
              el.style.left = `${newPosition.left}px`;
              el.style.top = `${newPosition.top}px`;
              el.style.transition = "none";
              el.dataset.snapPosition = JSON.stringify(newPosition);
            }
          }
          setShowQuickActions(false);
          // Limpiar el flag después de un delay más largo para evitar interferencias con fitContentHeight
          setTimeout(() => {
            isSnappingRef.current = false;
            const el = resizableRef.current?.resizable?.parentElement;
            if (el) {
              el.style.transition = "";
              el.dataset.snapPosition = "";
            }
          }, 800);
        }
        return; // Salir temprano para evitar ejecutar el código de actualización al final
      case "snap-bottom-left":
        isSnappingRef.current = true; // Marcar que estamos haciendo snap ANTES de cualquier cambio
        // Si está en fullscreen, primero desactivarlo
        if (isFullscreen) {
          setIsFullscreen(false);
          // Esperar un frame para que el estado se actualice, luego ejecutar el snap
          requestAnimationFrame(() => {
            // Calcular tamaño correcto: (w - 3*BORDER_MARGIN) / 2 para que quepan dos modales lado a lado
            const quarterWidth = (w - BORDER_MARGIN * 3) / 2;
            const quarterHeight = (h - BORDER_MARGIN * 3) / 2;
            newSize = { width: quarterWidth, height: quarterHeight };
            newPosition = { top: topbar + quarterHeight + BORDER_MARGIN * 2, left: currentSidebarWidth + BORDER_MARGIN };
            // Actualizar tamaño y posición de forma síncrona
            if (Object.keys(newSize).length) resizableRef.current.updateSize(newSize);
            if (Object.keys(newPosition).length) {
              updateModalPosition(newPosition);
              // Forzar actualización inmediata en el DOM
              const el = resizableRef.current?.resizable?.parentElement;
              if (el) {
                el.style.left = `${newPosition.left}px`;
                el.style.top = `${newPosition.top}px`;
                el.style.transition = "none";
                el.dataset.snapPosition = JSON.stringify(newPosition);
              }
            }
            setShowQuickActions(false);
            // Limpiar el flag después de un delay más largo para evitar interferencias
            setTimeout(() => {
              isSnappingRef.current = false;
              const el = resizableRef.current?.resizable?.parentElement;
              if (el) {
                el.style.transition = "";
                el.dataset.snapPosition = "";
              }
            }, 800);
          });
          return; // Salir temprano para evitar ejecutar el código de actualización al final
        } else {
          // Si no está en fullscreen, ejecutar el snap directamente
          // Calcular tamaño correcto: (w - 3*BORDER_MARGIN) / 2 para que quepan dos modales lado a lado
          const quarterWidth = (w - BORDER_MARGIN * 3) / 2;
          const quarterHeight = (h - BORDER_MARGIN * 3) / 2;
          newSize = { width: quarterWidth, height: quarterHeight };
          newPosition = { top: topbar + quarterHeight + BORDER_MARGIN * 2, left: currentSidebarWidth + BORDER_MARGIN };
          // Actualizar tamaño y posición de forma síncrona
          if (Object.keys(newSize).length) resizableRef.current.updateSize(newSize);
          if (Object.keys(newPosition).length) {
            updateModalPosition(newPosition);
            // Forzar actualización inmediata en el DOM
            const el = resizableRef.current?.resizable?.parentElement;
            if (el) {
              el.style.left = `${newPosition.left}px`;
              el.style.top = `${newPosition.top}px`;
              el.style.transition = "none";
              el.dataset.snapPosition = JSON.stringify(newPosition);
            }
          }
          setShowQuickActions(false);
          // Limpiar el flag después de un delay más largo para evitar interferencias con fitContentHeight
          setTimeout(() => {
            isSnappingRef.current = false;
            const el = resizableRef.current?.resizable?.parentElement;
            if (el) {
              el.style.transition = "";
              el.dataset.snapPosition = "";
            }
          }, 800);
        }
        return; // Salir temprano para evitar ejecutar el código de actualización al final
      case "snap-bottom-right":
        isSnappingRef.current = true; // Marcar que estamos haciendo snap ANTES de cualquier cambio
        // Si está en fullscreen, primero desactivarlo
        if (isFullscreen) {
          setIsFullscreen(false);
          // Esperar un frame para que el estado se actualice, luego ejecutar el snap
          requestAnimationFrame(() => {
            // Calcular tamaño correcto: (w - 3*BORDER_MARGIN) / 2 para que quepan dos modales lado a lado
            const quarterWidth = (w - BORDER_MARGIN * 3) / 2;
            const quarterHeight = (h - BORDER_MARGIN * 3) / 2;
            newSize = { width: quarterWidth, height: quarterHeight };
            newPosition = { top: topbar + quarterHeight + BORDER_MARGIN * 2, left: currentSidebarWidth + quarterWidth + BORDER_MARGIN * 2 };
            // Actualizar tamaño y posición de forma síncrona
            if (Object.keys(newSize).length) resizableRef.current.updateSize(newSize);
            if (Object.keys(newPosition).length) {
              updateModalPosition(newPosition);
              // Forzar actualización inmediata en el DOM
              const el = resizableRef.current?.resizable?.parentElement;
              if (el) {
                el.style.left = `${newPosition.left}px`;
                el.style.top = `${newPosition.top}px`;
                el.style.transition = "none";
                el.dataset.snapPosition = JSON.stringify(newPosition);
              }
            }
            setShowQuickActions(false);
            // Limpiar el flag después de un delay más largo para evitar interferencias
            setTimeout(() => {
              isSnappingRef.current = false;
              const el = resizableRef.current?.resizable?.parentElement;
              if (el) {
                el.style.transition = "";
                el.dataset.snapPosition = "";
              }
            }, 800);
          });
          return; // Salir temprano para evitar ejecutar el código de actualización al final
        } else {
          // Si no está en fullscreen, ejecutar el snap directamente
          // Calcular tamaño correcto: (w - 3*BORDER_MARGIN) / 2 para que quepan dos modales lado a lado
          const quarterWidth = (w - BORDER_MARGIN * 3) / 2;
          const quarterHeight = (h - BORDER_MARGIN * 3) / 2;
          newSize = { width: quarterWidth, height: quarterHeight };
          newPosition = { top: topbar + quarterHeight + BORDER_MARGIN * 2, left: currentSidebarWidth + quarterWidth + BORDER_MARGIN * 2 };
          // Actualizar tamaño y posición de forma síncrona
          if (Object.keys(newSize).length) resizableRef.current.updateSize(newSize);
          if (Object.keys(newPosition).length) {
            updateModalPosition(newPosition);
            // Forzar actualización inmediata en el DOM
            const el = resizableRef.current?.resizable?.parentElement;
            if (el) {
              el.style.left = `${newPosition.left}px`;
              el.style.top = `${newPosition.top}px`;
              el.style.transition = "none";
              el.dataset.snapPosition = JSON.stringify(newPosition);
            }
          }
          setShowQuickActions(false);
          // Limpiar el flag después de un delay más largo para evitar interferencias con fitContentHeight
          setTimeout(() => {
            isSnappingRef.current = false;
            const el = resizableRef.current?.resizable?.parentElement;
            if (el) {
              el.style.transition = "";
              el.dataset.snapPosition = "";
            }
          }, 800);
        }
        return; // Salir temprano para evitar ejecutar el código de actualización al final
      case "center":
        // Si está en fullscreen, primero desactivarlo y luego hacer center & resize
        if (isFullscreen) {
          setIsFullscreen(false);
          // Esperar un frame para que el estado se actualice, luego calcular tamaño y centrar
          requestAnimationFrame(() => {
            // Calcular el tamaño responsive (usa defaultSizes si están disponibles)
            const responsiveSizes = calculateResponsiveSizes();
            newSize = { width: responsiveSizes.width, height: responsiveSizes.height };
            newPosition = {
              top: Math.max(topbar + (h - responsiveSizes.height) / 2, topbar + BORDER_MARGIN),
              left: Math.max(currentSidebarWidth + (w - responsiveSizes.width) / 2, currentSidebarWidth + BORDER_MARGIN),
            };
            // Actualizar tamaño y posición
            if (Object.keys(newSize).length) resizableRef.current.updateSize(newSize);
            if (Object.keys(newPosition).length) updateModalPosition(newPosition);
            setShowQuickActions(false);
          });
          return; // Salir temprano para evitar ejecutar el código de actualización al final
        } else {
          // Si no está en fullscreen, solo centrar con el tamaño actual
          const sizeToUse = Object.keys(newSize).length ? newSize : currentSize;
          newPosition = {
            top: Math.max(topbar + (h - sizeToUse.height) / 2, topbar + BORDER_MARGIN),
            left: Math.max(currentSidebarWidth + (w - sizeToUse.width) / 2, currentSidebarWidth + BORDER_MARGIN),
          };
        }
        break;
      default:
        break;
    }

    // Para snap-left, snap-right y los cuartos, ya se actualizó arriba, así que solo actualizar otros casos
    if (
      type !== "snap-left" &&
      type !== "snap-right" &&
      type !== "snap-top-left" &&
      type !== "snap-top-right" &&
      type !== "snap-bottom-left" &&
      type !== "snap-bottom-right"
    ) {
      if (Object.keys(newSize).length) resizableRef.current.updateSize(newSize);
      if (Object.keys(newPosition).length) updateModalPosition(newPosition);
      setShowQuickActions(false);
    }
  };

  const QuickActionsButtons = () => {
    const buttonStyle = {
      minWidth: "32px",
      height: "32px",
      padding: "6px",
      borderRadius: "6px",
      color: colors.blueGrey[600],
      backgroundColor: "transparent",
      border: `1px solid ${colors.grey[300]}`,
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: colors.grey[100],
        borderColor: colors.blueGrey[400],
        color: colors.blueGrey[800],
      },
    };

    const quickActions = [
      { action: "center", icon: <CenterFocusWeak sx={{ fontSize: 16 }} />, tooltip: "Centrar", label: "Centrar" },
      {
        action: "fullscreen",
        icon: isFullscreen ? <FullscreenExit sx={{ fontSize: 16 }} /> : <Fullscreen sx={{ fontSize: 16 }} />,
        tooltip: isFullscreen ? "Salir pantalla completa" : "Pantalla completa",
        label: isFullscreen ? "Salir pantalla completa" : "Pantalla completa",
      },
      {
        action: "snap-left",
        icon: <CallSplit sx={{ fontSize: 16, transform: "rotate(90deg)" }} />,
        tooltip: "Ajustar izquierda",
        label: "Ajustar izquierda",
      },
      {
        action: "snap-right",
        icon: <CallSplit sx={{ fontSize: 16, transform: "rotate(270deg)" }} />,
        tooltip: "Ajustar derecha",
        label: "Ajustar derecha",
      },
      { action: "snap-top-left", tooltip: "Superior izquierda", label: "Superior izquierda" },
      { action: "snap-top-right", tooltip: "Superior derecha", label: "Superior derecha" },
      { action: "snap-bottom-left", tooltip: "Inferior izquierda", label: "Inferior izquierda" },
      { action: "snap-bottom-right", tooltip: "Inferior derecha", label: "Inferior derecha" },
    ];

    const handleMenuOpen = (event) => {
      setMenuAnchor(event.currentTarget);
      setMenuAnchorPosition({ top: event.clientY, left: event.clientX });
    };

    const handleMenuClose = () => {
      setMenuAnchor(null);
      setMenuAnchorPosition(null);
    };

    const handleMenuAction = (action) => {
      quickResize(action);
      handleMenuClose();
    };

    // Si el modal es pequeño, mostrar menú de puntos
    if (isSmallModal) {
      return (
        <>
          <Tooltip title="Más opciones" placement="bottom">
            <IconButton onClick={handleMenuOpen} sx={buttonStyle}>
              <MoreVert sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorReference="anchorPosition"
            anchorPosition={menuAnchorPosition}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            {quickActions.map(({ action, label, icon }) => (
              <MenuItem key={action} onClick={() => handleMenuAction(action)}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {icon}
                  {label}
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </>
      );
    }

    // Modal grande: mostrar botones normales
    return (
      <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 0.5 }}>
        <ButtonGroup
          variant="outlined"
          size="small"
          sx={{
            "& .MuiButtonGroup-grouped": {
              ...buttonStyle,
              borderColor: colors.grey[300],
              "&:not(:last-of-type)": { borderRightColor: colors.grey[300] },
            },
          }}
        >
          {quickActions.slice(0, 4).map(({ action, icon, tooltip }) => (
            <Tooltip key={action} title={tooltip} placement="bottom">
              <IconButton onClick={() => quickResize(action)} sx={buttonStyle}>
                {icon}
              </IconButton>
            </Tooltip>
          ))}

          <Tooltip title="Más opciones" placement="bottom">
            <IconButton
              onClick={() => setShowQuickActions(!showQuickActions)}
              sx={{ ...buttonStyle, backgroundColor: showQuickActions ? colors.grey[100] : "transparent" }}
            >
              <GridView sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </ButtonGroup>

        {showQuickActions && (
          <Box
            sx={{
              position: "absolute",
              top: "100%",
              right: 0,
              mt: 1,
              backgroundColor: "var(--fx-surface-base, #FFFFFF)",
              border: `1px solid ${colors.grey[300]}`,
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              zIndex: 1000,
              p: 1,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 0.5,
              minWidth: "120px",
            }}
          >
            {[
              { action: "snap-top-left", tooltip: "Superior izquierda" },
              { action: "snap-top-right", tooltip: "Superior derecha" },
              { action: "snap-bottom-left", tooltip: "Inferior izquierda" },
              { action: "snap-bottom-right", tooltip: "Inferior derecha" },
            ].map(({ action, tooltip }) => (
              <Tooltip key={action} title={tooltip} placement="left">
                <IconButton sx={{ minWidth: "28px", height: "28px" }} onClick={() => quickResize(action)}>
                  <CropFree sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // Handler para traer el modal al frente cuando se hace click en cualquier parte
  const handleModalClick = (e) => {
    // Solo traer al frente si el click fue directamente en el contenedor del modal
    // o en el Resizable, no en elementos interactivos dentro del contenido
    const target = e.target;
    const modalEl = resizableRef.current?.resizable?.parentElement;

    // Verificar que el click no fue en un elemento interactivo (botones, inputs, links, etc.)
    const isInteractiveElement = target.closest("button, a, input, select, textarea, [role='button'], [role='link']");

    // Si el click fue en el contenedor del modal o en el Resizable, traer al frente
    if (!isInteractiveElement && modalEl) {
      // Verificar que no hay un modal con z-index mayor en el punto del click
      const clickX = e.clientX;
      const clickY = e.clientY;
      const allModals = document.querySelectorAll('[style*="position: fixed"][style*="z-index"]');
      let hasModalAbove = false;
      const currentZIndex = parseInt(window.getComputedStyle(modalEl).zIndex) || 0;

      allModals.forEach((modal) => {
        if (modal === modalEl) return;
        const modalZIndex = parseInt(window.getComputedStyle(modal).zIndex) || 0;
        const rect = modal.getBoundingClientRect();

        // Si hay un modal con z-index mayor en el punto del click, no traer al frente
        if (modalZIndex > currentZIndex && clickX >= rect.left && clickX <= rect.right && clickY >= rect.top && clickY <= rect.bottom) {
          hasModalAbove = true;
        }
      });

      if (!hasModalAbove) {
        bringToFront(id);
      }
    }
  };

  const modalContent = (
    <motion.div
      key={id}
      initial={{ opacity: 0, scale: 0.2 }}
      animate={{
        opacity: 1,
        scale: 1,
        // Mantener control manual de posición - no usar transform de framer-motion
        x: 0,
        y: 0,
      }}
      exit={{ opacity: 0, scale: 0 }}
      transition={{
        duration: isDragging || resizing ? 0 : 0.4,
        scale: { type: "spring", visualDuration: 0.4, bounce: 0.5 },
      }}
      onClick={handleModalClick}
      onPointerDown={() => {
        window.dispatchEvent(new CustomEvent("app-modal-activate"));
      }}
      data-app-modal
      data-prevent-parent-trigger={preventParentTrigger ? "true" : undefined}
      style={{
        position: "fixed",
        top: initialTop,
        left: initialLeft,
        zIndex: zIndex || 15000 + index,
        cursor: isDragging ? "grabbing" : "default",
        userSelect: "none",
        backgroundColor: "transparent",
        pointerEvents: "auto",
        // Asegurar que transform solo se use para scale, no para posicionamiento
        transformOrigin: "center center",
      }}
    >
      <Resizable
        ref={resizableRef}
        defaultSize={
          isFullscreen || defaultFullscreen
            ? { width: availableWidth - BORDER_MARGIN * 2, height: getMaxModalHeight(availableHeight) }
            : calculateResponsiveSizes()
        }
        minWidth={Math.min(300, availableWidth * 0.5)}
        minHeight={Math.min(200, availableHeight * 0.3)}
        maxWidth={availableWidth - BORDER_MARGIN * 2}
        maxHeight={getMaxModalHeight(availableHeight)}
        style={{
          marginTop: MODAL_TOP_GAP,
          borderRadius: 8,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${colors.grey[200]}`,
          // Smooth size changes (e.g. quick actions / fitContentHeight) without affecting manual drag-resize.
          transition: resizing || isTransitioning ? "none" : "width 220ms cubic-bezier(0.2, 0, 0, 1), height 220ms cubic-bezier(0.2, 0, 0, 1)",
        }}
        onResizeStart={(e, direction) => {
          setResizing(true);
          const currentSize = resizableRef.current?.size || { width: 0, height: 0 };
          previousSizeRef.current = { width: currentSize.width, height: currentSize.height };

          // Detectar la dirección del resize
          // direction puede ser un string o un objeto
          let isLeft = false;
          let isTop = false;

          if (typeof direction === "string") {
            isLeft = direction === "left";
            isTop = direction === "top";
          } else if (direction && typeof direction === "object") {
            // Puede tener propiedades como { left: true } o ser un objeto con handle
            isLeft = !!direction.left;
            isTop = !!direction.top;
          }

          // Si no se detecta en direction, intentar detectar por la posición del mouse
          if (!isLeft && !isTop && e && e.target) {
            const rect = resizableRef.current?.resizable?.getBoundingClientRect();
            if (rect) {
              const mouseX = e.clientX || e.touches?.[0]?.clientX;
              const mouseY = e.clientY || e.touches?.[0]?.clientY;

              // Si el mouse está cerca del borde izquierdo, es resize desde la izquierda
              if (mouseX !== undefined && Math.abs(mouseX - rect.left) < 20) {
                isLeft = true;
              }
              // Si el mouse está cerca del borde superior, es resize desde arriba
              if (mouseY !== undefined && Math.abs(mouseY - rect.top) < 20) {
                isTop = true;
              }
            }
          }

          resizeDirectionRef.current = {
            left: isLeft,
            top: isTop,
          };
        }}
        onResize={(e, direction, ref, d) => {
          if (!resizeDirectionRef.current.left && !resizeDirectionRef.current.top) return;

          const currentSize = resizableRef.current?.size;
          if (!currentSize) return;

          const sizeDelta = {
            width: currentSize.width - previousSizeRef.current.width,
            height: currentSize.height - previousSizeRef.current.height,
          };

          // Si no hay cambio de tamaño, no hacer nada
          if (sizeDelta.width === 0 && sizeDelta.height === 0) return;

          const el = resizableRef.current?.resizable?.parentElement;
          if (!el) return;

          // Obtener la posición real del elemento (incluyendo cualquier transform de framer-motion)
          // Esto asegura que usamos la posición correcta después del drag
          const rect = el.getBoundingClientRect();
          let newX = rect.left;
          let newY = rect.top;

          // Sincronizar el estado con la posición real
          if (Math.abs(rect.left - currentPosition.x) > 1 || Math.abs(rect.top - currentPosition.y) > 1) {
            setCurrentPosition({ x: rect.left, y: rect.top });
          }

          // Si se está redimensionando desde la izquierda, mover el modal hacia la izquierda
          // El delta de ancho es positivo cuando crece, así que movemos hacia la izquierda
          if (resizeDirectionRef.current.left && sizeDelta.width !== 0) {
            newX = currentPosition.x - sizeDelta.width;
          }

          // Si se está redimensionando desde arriba, mover el modal hacia arriba
          // El delta de altura es positivo cuando crece, así que movemos hacia arriba
          if (resizeDirectionRef.current.top && sizeDelta.height !== 0) {
            newY = currentPosition.y - sizeDelta.height;
          }

          // Asegurar que el modal no salga de los límites de la ventana
          const minX = sidebarWidth + BORDER_MARGIN;
          const minY = TOPBAR_HEIGHT + BORDER_MARGIN;
          const maxX = windowSize.width - currentSize.width - BORDER_MARGIN;
          const maxY = windowSize.height - currentSize.height - BORDER_MARGIN;

          newX = Math.max(minX, Math.min(newX, maxX));
          newY = Math.max(minY, Math.min(newY, maxY));

          // Actualizar posición solo si cambió
          if (newX !== rect.left || newY !== rect.top) {
            el.style.left = `${newX}px`;
            el.style.top = `${newY}px`;
            setCurrentPosition({ x: newX, y: newY });
          }

          previousSizeRef.current = { width: currentSize.width, height: currentSize.height };
        }}
        onResizeStop={() => {
          // Limpiar estado de resizing inmediatamente
          resizeDirectionRef.current = { left: false, top: false };

          // Usar requestAnimationFrame para asegurar que el estado se actualice después del render
          requestAnimationFrame(() => {
            setResizing(false);

            // Restaurar cursor
            document.body.style.cursor = "";
            document.body.style.userSelect = "";

            // Verificar tamaño después del resize
            const currentSize = resizableRef.current?.size;
            if (currentSize) {
              const SMALL_MODAL_THRESHOLD = 500; // px
              setIsSmallModal(currentSize.width < SMALL_MODAL_THRESHOLD);

              // Validar y corregir posición final para asegurar que el modal esté dentro de los límites
              const el = resizableRef.current?.resizable?.parentElement;
              if (el) {
                // Obtener la posición real del elemento
                const rect = el.getBoundingClientRect();
                const minX = sidebarWidth + BORDER_MARGIN;
                const minY = TOPBAR_HEIGHT + BORDER_MARGIN;
                const maxX = windowSize.width - currentSize.width - BORDER_MARGIN;
                const maxY = windowSize.height - currentSize.height - BORDER_MARGIN;

                const constrainedX = Math.max(minX, Math.min(rect.left, maxX));
                const constrainedY = Math.max(minY, Math.min(rect.top, maxY));

                el.style.left = `${constrainedX}px`;
                el.style.top = `${constrainedY}px`;
                setCurrentPosition({ x: constrainedX, y: constrainedY });
              }
            }
          });
        }}
      >
        <div
          data-modal-drag-handle
          style={{
            height: HEADER_HEIGHT,
            width: "100%",
            backgroundColor: colors.grey[50],
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 16px",
            userSelect: "none",
            cursor: resizing || isTransitioning ? "default" : "grab",
            borderRadius: "8px 8px 0 0",
            pointerEvents: resizing || isTransitioning ? "none" : "auto",
            boxSizing: "border-box",
            flexShrink: 0,
            borderBottom: `1px solid ${colors.grey[200]}`,
          }}
          onMouseDown={handleMouseDown}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Box
              sx={{
                color: colors.blueGrey[700],
                fontSize: { xs: 11, sm: 12, md: 14, lg: 15 },
                fontWeight: 600,
                letterSpacing: "0.02em",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {modalLabel.replace(/\b\w/g, (c) => c.toUpperCase())}
            </Box>
            {quickResizePosition === "left" && <QuickActionsButtons />}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, transform: "translateY(4px)" }}>
            {quickResizePosition === "right" && <QuickActionsButtons />}

            <Tooltip title="Minimizar">
              <IconButton
                onClick={() => minimizeModal(id)}
                sx={{
                  minWidth: "32px",
                  height: "32px",
                  padding: "6px",
                  color: colors.blueGrey[600],
                  "&:hover": { backgroundColor: colors.grey[100], color: colors.blueGrey[800] },
                }}
              >
                <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>
                  &ndash;
                </Box>
              </IconButton>
            </Tooltip>

            <Tooltip title="Cerrar">
              <IconButton
                onClick={() => {
                  if (onConfirmClose) {
                    showDialog({
                      type: "confirm",
                      title: "Close modal?",
                      message: "Are you sure you want to close this modal? Unsaved changes will be lost.",
                      confirmLabel: "Yes, close",
                      cancelLabel: "Cancel",
                      onConfirm: () => {
                        const allowClose = onConfirmClose() !== false;
                        if (!allowClose) {
                          return;
                        }
                        setTimeout(() => {
                          handleClose();
                        }, 0);
                      },
                    });
                  } else {
                    handleClose();
                  }
                }}
                sx={{
                  minWidth: "32px",
                  height: "32px",
                  padding: "6px",
                  backgroundColor: colors.blue[100],
                  color: colors.blueGrey[600],
                  "&:hover": {
                    backgroundColor: colors.blue[200],
                    color: colors.blueGrey[600],
                  },
                }}
              >
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        <div
          ref={contentContainerRef}
          style={{
            flex: 1,
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
            //backgroundColor: "green",
            height: "100%",
            borderRadius: enableFooterDrag ? "0" : "0 0 8px 8px",
            minHeight: 0, // Permite que flex funcione correctamente
            display: "flex", // Necesario para que el hijo con height: 100% funcione
            flexDirection: "column",
          }}
        >
          <div
            ref={contentInnerRef}
            style={{
              display: "flex",
              flexDirection: "column",
              flex: "1 0 auto",
              minHeight: "100%",
              width: "100%",
              alignSelf: "stretch",
            }}
          >
            {content}
          </div>
        </div>

        {enableFooterDrag && (
          <div
            data-modal-drag-handle
            style={{
              height: FOOTER_HEIGHT,
              width: "100%",
              backgroundColor: colors.grey[50],
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 16px",
              userSelect: "none",
              cursor: resizing || isTransitioning ? "default" : "grab",
              borderRadius: "0 0 8px 8px",
              pointerEvents: resizing || isTransitioning ? "none" : "auto",
              boxSizing: "border-box",
              flexShrink: 0,
              borderTop: `1px solid ${colors.grey[200]}`,
            }}
            onMouseDown={handleMouseDown}
          >
            <div style={{ flex: 1 }} />
          </div>
        )}
      </Resizable>
    </motion.div>
  );

  return modalContent;
};

/**
 * BaseModalComponent - Resizable and draggable modal component
 *
 * Main component for rendering modals in the application. Supports manual drag,
 * resize from all sides, nested modals, and responsive sizes with breakpoints.
 * This is the most used component in the application for rendering content in modals.
 *
 * IMPORTANT - Avoiding duplicate modals: Each modal in context is rendered by every
 * BaseModalComponent whose modalKey matches. Use only ONE instance per modalKey in
 * the tree. For list items, mount one BaseModal in the parent and open via
 * openModal(sharedKey, content). See BASEMODAL.md in this folder.
 *
 * @component
 * @param {React.ReactElement} [elementTrigger] - Element that acts as trigger to open the modal.
 *                                                 If provided, it will be cloned and onClick handler will be added.
 *                                                 **Optional**. If not provided, modal must be opened programmatically.
 * @example
 * // With trigger button
 * <BaseModalComponent
 *   elementTrigger={<Button>Open Modal</Button>}
 *   modalKey="my-modal"
 * >
 *   <MyContent />
 * </BaseModalComponent>
 *
 * @param {React.ReactNode} children - Content to be rendered inside the modal.
 *                                     **REQUIRED**. Can be any React component.
 * @example
 * <BaseModalComponent modalKey="example">
 *   <div>My content here</div>
 * </BaseModalComponent>
 *
 * @param {string} [modalKey="default"] - Unique identifier for the modal. Used to manage multiple
 *                                        instances of the same modal and to identify specific modals.
 *                                        **Optional**, defaults to "default".
 *                                        Must be unique if there are multiple modals of the same type.
 * @example
 * modalKey="Dashboard-Jobs-List"
 * modalKey="User-Profile-Edit"
 *
 * @param {string} [modalLabel="Flownect"] - Label/title displayed in the modal header.
 *                                           **Optional**, defaults to "Flownect".
 *                                           Displayed with capitalized format in the header.
 * @example
 * modalLabel="My Jobs"
 * modalLabel="Create Workforce"
 *
 * @param {Object|Object<string|number|Object>} [defaultSizes] - Initial sizes of the modal.
 *                                                               **Optional**. If not provided, sizes are calculated automatically.
 *                                                               Supports fixed values (number/string) or responsive objects with breakpoints.
 *
 * @param {number|string|Object} [defaultSizes.width] - Initial width of the modal.
 *                                                       Can be:
 *                                                       - **number**: Fixed pixels (e.g., 800)
 *                                                       - **string with px**: Fixed pixels (e.g., "800px")
 *                                                       - **string with %**: Percentage of available viewport (e.g., "90%")
 *                                                       - **Object with breakpoints**: {xs: "90%", sm: "80%", md: "90%", lg: "1600px"}
 * @example
 * // Fixed value in pixels
 * defaultSizes={{ width: 1200, height: 800 }}
 *
 * // Fixed value with string
 * defaultSizes={{ width: "1200px", height: "800px" }}
 *
 * // Viewport percentage
 * defaultSizes={{ width: "90%", height: "85%" }}
 *
 * // Responsive with breakpoints (xs, sm, md, lg)
 * defaultSizes={{
 *   width: { xs: "90%", sm: "80%", md: "90%", lg: "1600px" },
 *   height: { xs: "95%", sm: "90%", md: "90%", lg: "90%" }
 * }}
 *
 * // Mixed: responsive width, fixed height
 * defaultSizes={{
 *   width: { xs: "95%", sm: "85%", md: "1200px", lg: "1600px" },
 *   height: 800
 * }}
 *
 * @param {number|string|Object} [defaultSizes.height] - Initial height of the modal.
 *                                                        Same format as width.
 *                                                        **Important**: If height is not provided, `fitContentHeight`
 *                                                        is automatically enabled to adjust height to content.
 * @example
 * // See examples in defaultSizes.width
 *
 * @param {"left"|"right"} [quickResizePosition="right"] - Position of quick action buttons (center, fullscreen, snap, etc.)
 *                                                         in the modal header.
 *                                                         **Optional**, defaults to "right".
 * @example
 * quickResizePosition="left"  // Buttons to the left of title
 * quickResizePosition="right" // Buttons to the right (default)
 *
 * @param {boolean} [defaultFullscreen] - If true, modal opens in fullscreen by default.
 *                                        **Optional**. If not provided, modal opens with calculated size
 *                                        or the one specified in defaultSizes.
 * @example
 * defaultFullscreen={true}  // Opens in fullscreen
 * defaultFullscreen={false} // Opens with normal size
 *
 * @param {Function} [onConfirmClose] - Callback executed before closing the modal.
 *                                      If provided, shows a confirmation dialog before closing.
 *                                      **Optional**. If not provided, modal closes directly.
 * @example
 * onConfirmClose={() => {
 *   // Logic before closing (e.g., save changes, validate, etc.)
 *   console.log("Closing modal...");
 * }}
 *
 * @param {boolean} [fitContentHeight] - If true, modal automatically adjusts its height to content.
 *                                       **Optional**. If not specified, automatically enabled when
 *                                       defaultSizes.height is not defined.
 * @example
 * fitContentHeight={true}  // Adjusts height to content
 * fitContentHeight={false} // Uses fixed or calculated height
 *
 * @param {boolean} [enableFooterDrag=true] - If true, shows footer and allows dragging modal from footer.
 *                                            **Optional**, defaults to true.
 *                                            If false, footer is not shown and only header is draggable.
 * @example
 * enableFooterDrag={true}  // Footer visible and draggable (default)
 * enableFooterDrag={false} // No footer, only header draggable
 *
 * @returns {React.ReactElement} Modal component rendered via portal in document.body
 *
 * @example
 * // Basic example
 * <BaseModalComponent
 *   modalKey="simple-modal"
 *   modalLabel="My Modal"
 * >
 *   <div>Modal content</div>
 * </BaseModalComponent>
 *
 * @example
 * // Complete example with all options
 * <BaseModalComponent
 *   modalKey="dashboard-jobs"
 *   modalLabel="Jobs List"
 *   elementTrigger={<Button>View Jobs</Button>}
 *   defaultSizes={{
 *     width: { xs: "90%", sm: "80%", md: "90%", lg: "1600px" },
 *     height: { xs: "95%", sm: "90%", md: "90%", lg: "90%" }
 *   }}
 *   quickResizePosition="right"
 *   defaultFullscreen={false}
 *   onConfirmClose={() => {
 *     if (hasUnsavedChanges) {
 *       return true; // Shows confirmation
 *     }
 *   }}
 *   fitContentHeight={false}
 *   enableFooterDrag={true}
 * >
 *   <JobsListComponent />
 * </BaseModalComponent>
 *
 * @example
 * // Example with percentage sizes
 * <BaseModalComponent
 *   modalKey="responsive-modal"
 *   defaultSizes={{
 *     width: "85%",  // 85% of available width
 *     height: "80%"  // 80% of available height
 *   }}
 * >
 *   <Content />
 * </BaseModalComponent>
 *
 * @example
 * // Example with fixed pixel sizes
 * <BaseModalComponent
 *   modalKey="fixed-size-modal"
 *   defaultSizes={{
 *     width: 1200,    // 1200px
 *     height: "800px" // 800px (also accepts string)
 *   }}
 * >
 *   <Content />
 * </BaseModalComponent>
 *
 * @example
 * // Example without trigger (programmatic opening)
 * const MyComponent = () => {
 *   const { openModal } = useModals();
 *
 *   const handleOpen = () => {
 *     openModal("my-modal-key", <MyContent />);
 *   };
 *
 *   return <Button onClick={handleOpen}>Open</Button>;
 * };
 */
export const BaseModalComponent = ({
  elementTrigger,
  children,
  modalKey = "default",
  modalLabel = "Flownect",
  defaultSizes,
  quickResizePosition = "right",
  defaultFullscreen,
  onConfirmClose,
  onClose,
  fitContentHeight = undefined,
  enableFooterDrag = true,
  preventParentTrigger = false,
  allowInsidePreventModal = false,
  disabled = false,
  preferModalContent = false,
}) => {
  console.count(`render BaseModalComponent: ${modalKey}`);

  const { modals, openModal, closeModal, minimizeModal, restoreModal, expandItem, bringToFront, registerModal, registerModalRenderer } = useModals();

  // Si no se especifica fitContentHeight, lo activamos automáticamente cuando no hay defaultSizes.height
  // Verificar si hay height en cualquier breakpoint
  const hasDefaultHeight =
    defaultSizes?.height &&
    (typeof defaultSizes.height === "number" ||
      typeof defaultSizes.height === "string" ||
      defaultSizes.height.xs ||
      defaultSizes.height.sm ||
      defaultSizes.height.md ||
      defaultSizes.height.lg);
  const shouldFitContentHeight = fitContentHeight !== undefined ? fitContentHeight : !hasDefaultHeight;

  const getModalContent = useCallback(() => {
    if (isValidElement(children)) {
      return cloneElement(children, { onConfirmClose });
    }

    return children;
  }, [children, onConfirmClose]);

  useEffect(() => {
    if (!registerModal || children == null || children === false) return;

    return registerModal(modalKey, {
      content: getModalContent(),
      onConfirmClose,
      preventParentTrigger,
      defaultSizes,
      defaultFullscreen,
      fitContentHeight: shouldFitContentHeight,
      enableFooterDrag,
      modalLabel,
      onClose,
      quickResizePosition,
    });
  }, [
    children,
    getModalContent,
    modalKey,
    onConfirmClose,
    preventParentTrigger,
    registerModal,
    defaultSizes,
    defaultFullscreen,
    shouldFitContentHeight,
    enableFooterDrag,
    modalLabel,
    onClose,
    quickResizePosition,
  ]);

  const handleOpen = () => {
    // Do not bail when already open — ModalContext merges same key (refresh content + z-index).
    openModal({ key: modalKey, title: modalLabel, preventParentTrigger }, getModalContent(), onConfirmClose);
  };

  // Función helper para verificar si un evento está dentro de un modal con preventParentTrigger
  const isEventInsidePreventModal = (e) => {
    const allModals = document.querySelectorAll('[data-prevent-parent-trigger="true"]');

    for (const modal of allModals) {
      const rect = modal.getBoundingClientRect();
      const eventX = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
      const eventY = e.clientY || (e.touches && e.touches[0]?.clientY) || 0;

      // Si el evento está dentro de un modal con preventParentTrigger, retornar true
      if (eventX >= rect.left && eventX <= rect.right && eventY >= rect.top && eventY <= rect.bottom) {
        return true;
      }
    }

    return false;
  };

  // Detecta si el trigger necesita ocupar el 100% del ancho
  // Verifica fullWidth prop, width en sx, o width en style
  const shouldTriggerBeFullWidth =
    elementTrigger &&
    (elementTrigger.props?.fullWidth === true ||
      elementTrigger.props?.sx?.width === "100%" ||
      elementTrigger.props?.style?.width === "100%" ||
      (typeof elementTrigger.props?.sx?.width === "object" && Object.values(elementTrigger.props.sx.width).some((value) => value === "100%")));

  const triggerElement = elementTrigger
    ? cloneElement(elementTrigger, {
        onClick: (e) => {
          if (typeof elementTrigger.props?.onClick === "function") {
            elementTrigger.props.onClick(e);
          }
          if (disabled) return;
          // Verificar si el click está dentro de un modal con preventParentTrigger
          if (!allowInsidePreventModal && isEventInsidePreventModal(e)) {
            e.stopPropagation();
            e.preventDefault();
            e.stopImmediatePropagation?.();
            return;
          }

          e.stopPropagation();
          handleOpen();
        },
        onMouseUp: (e) => {
          if (disabled) return;
          // Si hay un modal hijo con preventParentTrigger, no abrir el modal padre
          if (!allowInsidePreventModal && isEventInsidePreventModal(e)) {
            e.stopPropagation();
            e.preventDefault();
            e.stopImmediatePropagation?.();
            return;
          }
        },
      })
    : null;

  return (
    <>
      <GlobalStyles
        styles={{
          ".MuiPopper-root": { zIndex: "50000 !important" },
          ".MuiAutocomplete-popper": { zIndex: "50000 !important" },
          ".MuiPopover-root": { zIndex: "50000 !important" },
          ".MuiMenu-root": { zIndex: "50000 !important" },
        }}
      />
      {preventParentTrigger && triggerElement ? (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            // Aplica width: 100% automáticamente si el trigger lo necesita
            ...(shouldTriggerBeFullWidth && { width: "100%" }),
          }}
        >
          {triggerElement}
        </Box>
      ) : (
        triggerElement
      )}
    </>
  );
};

export const GlobalModalStack = () => {
  const { modals, closeModal, minimizeModal, restoreModal, expandItem, bringToFront, getModalConfig } = useModals();

  const activeModals = modals.filter((modal) => !modal.minimized);

  if (activeModals.length === 0) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      <GlobalStyles
        styles={{
          ".MuiPopper-root": { zIndex: "50000 !important" },
          ".MuiAutocomplete-popper": { zIndex: "50000 !important" },
          ".MuiPopover-root": { zIndex: "50000 !important" },
          ".MuiMenu-root": { zIndex: "50000 !important" },
        }}
      />
      <Box
        key="global-modal-overlay"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.02)",
          backdropFilter: !!process.env.REACT_APP_BASEMODAL_BACKDROP_FILTER ? "blur(2px)" : "none !important",
          zIndex: 14000,
          pointerEvents: "none",
          transition: "all 0.3s ease-in-out",
        }}
      />
      <AnimatePresence>
        {activeModals.map(({ id, key, title, label, content, minimized, zIndex, onConfirmClose: modalCallback, preventParentTrigger }, index) => {
          const config = getModalConfig?.(key) || {};
          const finalContent = content || config.content;

          return (
            <ModalItem
              expandItem={expandItem}
              defaultSizes={config.defaultSizes}
              key={id}
              modalLabel={title || label || config.modalLabel || finalContent?.props?.modalLabel || key}
              id={id}
              defaultFullscreen={config.defaultFullscreen}
              modalKey={key}
              index={index}
              bringToFront={bringToFront}
              zIndex={zIndex}
              content={finalContent?.type ? cloneElement(finalContent, {}) : finalContent}
              closeModal={closeModal}
              minimizeModal={minimizeModal}
              restoreModal={restoreModal}
              minimized={minimized}
              onConfirmClose={modalCallback || config.onConfirmClose}
              onClose={config.onClose}
              quickResizePosition={config.quickResizePosition || "right"}
              fitContentHeight={config.fitContentHeight ?? true}
              enableFooterDrag={config.enableFooterDrag ?? true}
              preventParentTrigger={preventParentTrigger || config.preventParentTrigger}
              windowSize={{
                width: window.innerWidth,
                height: window.innerHeight,
              }}
            />
          );
        })}
      </AnimatePresence>
    </>,
    document.body,
  );
};

// PropTypes definition for TypeScript/IDE support
BaseModalComponent.propTypes = {
  /** Element that acts as trigger to open the modal. If provided, it will be cloned and onClick handler will be added. */
  elementTrigger: PropTypes.element,

  /** Content to be rendered inside the modal. Optional when opening only via openModal(key, content). */
  children: PropTypes.node,

  /** Unique identifier for the modal. Used to manage multiple instances of the same modal. Defaults to "default". */
  modalKey: PropTypes.string,

  /** Label/title displayed in the modal header. Defaults to "Flownect". */
  modalLabel: PropTypes.string,

  /** Initial sizes of the modal. Supports fixed values (number/string) or responsive objects with breakpoints (xs, sm, md, lg). */
  defaultSizes: PropTypes.shape({
    /** Width: number (px), string ("800px" or "90%"), or object with breakpoints {xs, sm, md, lg} */
    width: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.shape({
        xs: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        sm: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        md: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        lg: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    ]),
    /** Height: number (px), string ("800px" or "90%"), or object with breakpoints {xs, sm, md, lg} */
    height: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.shape({
        xs: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        sm: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        md: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        lg: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    ]),
  }),

  /** Position of quick action buttons in the modal header. "left" or "right". Defaults to "right". */
  quickResizePosition: PropTypes.oneOf(["left", "right"]),

  /** If true, modal opens in fullscreen by default. */
  defaultFullscreen: PropTypes.bool,

  /** Callback executed before closing the modal. If provided, shows a confirmation dialog. */
  onConfirmClose: PropTypes.func,

  /** Callback executed when the modal is closed from its own close controls. */
  onClose: PropTypes.func,

  /** If true, modal automatically adjusts its height to content. Auto-enabled if defaultSizes.height is not defined. */
  fitContentHeight: PropTypes.bool,

  /** If true, shows footer and allows dragging modal from footer. Defaults to true. */
  enableFooterDrag: PropTypes.bool,

  /** If true, prevents the parent modal's trigger from opening when events occur inside this modal. Useful when this modal is nested inside another modal's elementTrigger. Defaults to false. */
  preventParentTrigger: PropTypes.bool,

  /** If true, allows this trigger to open from inside a modal that uses preventParentTrigger. Defaults to false. */
  allowInsidePreventModal: PropTypes.bool,

  /** If true, programmatic openModal content is rendered before static children. Defaults to false. */
  preferModalContent: PropTypes.bool,
};
